type CacheEntry<T> = { value: T; expires: number };

const inflight = new Map<string, Promise<unknown>>();
const results = new Map<string, CacheEntry<unknown>>();

/**
 * Coalesce concurrent identical requests and optionally cache results briefly.
 */
export function dedupeAsync<T>(
  key: string,
  factory: () => Promise<T>,
  cacheMs = 0
): Promise<T> {
  if (cacheMs > 0) {
    const hit = results.get(key);
    if (hit && hit.expires > Date.now()) {
      return Promise.resolve(hit.value as T);
    }
  }

  const running = inflight.get(key);
  if (running) {
    return running as Promise<T>;
  }

  const promise = factory()
    .then((data) => {
      if (cacheMs > 0) {
        results.set(key, { value: data, expires: Date.now() + cacheMs });
      }
      return data;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
}

export function invalidateDedupeCache(keyPrefix: string): void {
  results.forEach((_entry, key) => {
    if (key.startsWith(keyPrefix)) {
      results.delete(key);
    }
  });
  inflight.forEach((_promise, key) => {
    if (key.startsWith(keyPrefix)) {
      inflight.delete(key);
    }
  });
}
